import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";

interface ImagePreviewProps {
  open: boolean;
  images: string[];
  currentIndex: number;
  title: string;
  onClose: () => void;
  onDelete?: () => void;
  onPrev: () => void;
  onNext: () => void;
  hideDelete?: boolean;
  loading?: boolean;
  deleteLoading?: boolean;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({
  open,
  images,
  currentIndex,
  title,
  onClose,
  onDelete,
  onPrev,
  onNext,
  hideDelete,
  loading = false,
  deleteLoading = false,
}) => {
  const { t } = useTranslation("dashboard");

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 10000 }}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "#E1F7E7",
          borderRadius: { xs: "22px", sm: "30px" },
          boxShadow: 24,
          p: { xs: "20px", sm: "24px" },
          width: "min(606px, calc(100vw - 24px))",
          maxHeight: "calc(100vh - 24px)",
          outline: "none",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            pb: { xs: 1.75, sm: 2 },
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            align="center"
            fontWeight={700}
            sx={{
              fontSize: {
                xs: `calc(16px * var(--dyn-font-size,1))`,
                sm: `calc(18px * var(--dyn-font-size,1))`,
              },
              lineHeight: 1.35,
              color: "#0F202B",
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            position: "relative",
            aspectRatio: "557 / 406",
            borderRadius: { xs: "14px", sm: "16px" },
            overflow: "hidden",
            backgroundColor: "rgba(255,255,255,0.55)",
          }}
        >
          {loading ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(225, 247, 231, 0.8)",
                zIndex: 1,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box
              component="img"
              src={images[currentIndex]}
              alt="preview"
              sx={{
                // maxWidth: { xs: "100%", sm: "100%", md: "100%" },
                // maxHeight: { xs: "50vh", sm: "50vh", md: "100%" },
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          )}
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={{ xs: 1.25, sm: 1.5 }}
          sx={{
            flexShrink: 0,
          }}
        >
          <Typography
            color={currentIndex === 0 ? "#8C9A91" : "#2E3B80"}
            fontWeight={600}
            sx={{
              cursor: currentIndex === 0 ? "not-allowed" : "pointer",
              userSelect: "none",
              px: 0.75,
              fontSize: {
                xs: `calc(13px * var(--dyn-font-size,1))`,
                sm: `calc(14px * var(--dyn-font-size,1))`,
              },
              transition: "color 0.2s",
              opacity: currentIndex === 0 ? 0.5 : 1,
            }}
            onClick={currentIndex === 0 ? undefined : onPrev}
          >
            {t("uploadPhotos.prev")}
          </Typography>
          <Typography
            mx={0.5}
            fontWeight={700}
            sx={{
              color: "black",
              fontSize: {
                xs: `calc(12px * var(--dyn-font-size,1))`,
                sm: `calc(13px * var(--dyn-font-size,1))`,
              },
            }}
          >
            {String(currentIndex + 1).padStart(2, "0")}/
            {String(images.length).padStart(2, "0")}
          </Typography>
          <Typography
            color={
              currentIndex === images.length - 1 ? "#8C9A91" : "#2E3B80"
            }
            fontWeight={600}
            sx={{
              cursor:
                currentIndex === images.length - 1 ? "not-allowed" : "pointer",
              userSelect: "none",
              px: 0.75,
              fontSize: {
                xs: `calc(13px * var(--dyn-font-size,1))`,
                sm: `calc(14px * var(--dyn-font-size,1))`,
              },
              transition: "color 0.2s",
              opacity: currentIndex === images.length - 1 ? 0.5 : 1,
            }}
            onClick={currentIndex === images.length - 1 ? undefined : onNext}
          >
            {t("uploadPhotos.next")}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent={hideDelete ? "center" : "space-between"}
          gap={{ xs: 1.25, sm: 2.25 }}
          sx={{
            flexShrink: 0,
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            sx={{
              bgcolor: "#106426",
              color: "#ffffff",
              borderRadius: "999px",
              px: 3,
              py: 1.25,
              fontWeight: 500,
              flex: hideDelete ? "0 1 267px" : 1,
              maxWidth: hideDelete ? "267px" : "none",
              fontSize: {
                xs: `calc(14px * var(--dyn-font-size,1))`,
                sm: `calc(16px * var(--dyn-font-size,1))`,
              },
              boxShadow: "none",
              "&:hover": { bgcolor: "#26336F", boxShadow: "none" },
            }}
            onClick={onClose}
          >
            {t("uploadPhotos.close")}
          </Button>
          {!hideDelete && (
            <Button
              variant="contained"
              sx={{
                bgcolor: "#106426",
                color: "#ffffff",
                borderRadius: "999px",
                px: 3,
                py: 1.25,
                fontWeight: 500,
                flex: 1,
                fontSize: {
                  xs: `calc(14px * var(--dyn-font-size,1))`,
                  sm: `calc(16px * var(--dyn-font-size,1))`,
                },
                boxShadow: "none",
                "&:hover": { bgcolor: "#26336F", boxShadow: "none" },
              }}
              onClick={onDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("uploadPhotos.delete")
              )}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ImagePreview;
